// Day-10 Slice A: 连接状态 banner（取代之前裸的 ws-state 文字）
//
// V2-2 修订：
//   - 1.5s debounce：state ≠ open 持续超过 1.5s 才显示，防 5-10s 抖动闪烁
//   - state = open 立即隐藏
//   - failed_over (切到 HK) 显示 5s 后转为静默——已恢复无需常驻
//   - position: absolute，不挤压聊天流（防 CLS）

import { useEffect, useRef, useState } from "preact/hooks";
import { wsState, wsStateDetail } from "../state/session";

const SHOW_DELAY_MS = 1500;
const FAILED_OVER_AUTO_HIDE_MS = 5000;

interface BannerSpec {
  text: string;
  cls: string;
  clickable?: boolean;
}

function specFor(state: string, detail: string): BannerSpec | null {
  switch (state) {
    case "open":
      return null;
    case "connecting":
      return { text: "连接中…", cls: "muted" };
    case "reconnecting": {
      // detail 形如 "1500ms"
      const isHK = detail?.includes("ear-hkg");
      return {
        text: isHK ? "已切到 HK 备线…" : "重连中…",
        cls: isHK ? "info" : "warn",
        clickable: true,
      };
    }
    case "fatal":
      return { text: "连接失败 · 点击重试", cls: "error", clickable: true };
    case "idle":
      return null;
    default:
      return null;
  }
}

interface Props {
  onRetry: () => void;
}

export function ConnectionBanner({ onRetry }: Props) {
  const state = wsState.value;
  const detail = wsStateDetail.value;
  const [visible, setVisible] = useState(false);
  const spec = specFor(state, detail);

  // V2-2 修订版 debounce：state ≠ open 持续 > 1.5s 才显示
  // 关键：state 在非-open 集合（connecting / reconnecting）内**互相切换**时不重置 timer，
  //       否则 ws.ts 在 reconnect 时 state 1s 切一次 banner 永远不出现。
  // 实现：用 ref 记录"首次进入非-open 态"的时间戳。
  const enteredAtRef = useRef<number | null>(null);

  useEffect(() => {
    // 回到 open / idle → 立即隐藏 banner + 清状态
    if (state === "open" || state === "idle") {
      enteredAtRef.current = null;
      setVisible(false);
      return;
    }
    // 首次进入非-open（之前没在重连状态）
    if (enteredAtRef.current === null) {
      enteredAtRef.current = Date.now();
      const id = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => clearTimeout(id);
    }
    // 已经在非-open 态中（state 从 reconnecting → connecting 这种切换）：
    // 不重置 timer，只补检查是否已超过阈值
    const elapsed = Date.now() - enteredAtRef.current;
    if (elapsed >= SHOW_DELAY_MS) {
      setVisible(true);
    } else {
      const id = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS - elapsed);
      return () => clearTimeout(id);
    }
  }, [state]);

  // failed_over auto hide（这里复用 detail 含 "ear-hkg"）
  useEffect(() => {
    if (visible && state === "reconnecting" && detail?.includes("ear-hkg")) {
      const id = window.setTimeout(() => setVisible(false), FAILED_OVER_AUTO_HIDE_MS);
      return () => clearTimeout(id);
    }
  }, [visible, state, detail]);

  if (!visible || !spec) return null;
  return (
    <div
      class={`banner banner--${spec.cls}`}
      onClick={spec.clickable ? onRetry : undefined}
      role="status"
    >
      {spec.text}
    </div>
  );
}
